import { useEffect, useState } from 'react';
import { Button, Group, Modal, Stack, TextInput } from '@mantine/core';

/**
 * MED-1131 — Text + URL link dialog for the shared Session Notes editor.
 *
 * Replaces the bare `window.prompt('Enter URL:')` that the editor used to add
 * hyperlinks. The old prompt collected only a URL and, with no text selected,
 * inserted nothing (UAT #45 issue 1). This dialog collects an optional display
 * Text and a required URL so a usable link always inserts.
 *
 * The dialog is a controlled, "dumb" component: it manages only its own field
 * state and reports the raw `(text, url)` on submit. URL scheme normalization
 * (AC6) and the actual TipTap insertion live in the editor so there is one
 * source of truth for how a link is built.
 */
export interface LinkDialogProps {
  /** Whether the dialog is open. */
  opened: boolean;
  /**
   * Pre-fill for the Text field — the current selection (AC3) or, when editing
   * an existing link, that link's text (AC7). Empty when nothing was selected.
   */
  initialText: string;
  /** Pre-fill for the URL field — an existing link's href (AC7), else empty. */
  initialUrl: string;
  /** Reports the entered Text (may be blank) and URL when the user confirms. */
  onSubmit: (text: string, url: string) => void;
  /** Close without inserting (Cancel / ✕ / Escape). */
  onClose: () => void;
}

export function LinkDialog({
  opened,
  initialText,
  initialUrl,
  onSubmit,
  onClose,
}: LinkDialogProps) {
  const [text, setText] = useState(initialText);
  const [url, setUrl] = useState(initialUrl);

  // Re-seed the fields each time the dialog opens so it reflects the current
  // selection / existing link rather than stale values from a prior open.
  useEffect(() => {
    if (opened) {
      setText(initialText);
      setUrl(initialUrl);
    }
  }, [opened, initialText, initialUrl]);

  // AC2 — URL is required; "Add link" stays disabled until it is non-empty.
  const canSubmit = url.trim() !== '';

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(text, url);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Add link"
      centered
      size="md"
      data-testid="rte-link-dialog"
    >
      <form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Stack gap="md">
          <TextInput
            label="Text"
            placeholder="Link text — defaults to the URL"
            value={text}
            onChange={e => setText(e.currentTarget.value)}
            data-autofocus
            data-testid="rte-link-text"
          />
          <TextInput
            label="URL"
            placeholder="https://example.com"
            required
            value={url}
            onChange={e => setUrl(e.currentTarget.value)}
            data-testid="rte-link-url"
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose} data-testid="rte-link-cancel">
              Cancel
            </Button>
            <Button
              type="submit"
              color="medleyGreen"
              disabled={!canSubmit}
              data-testid="rte-link-submit"
            >
              Add link
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export default LinkDialog;
